import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

import { useUserStore } from '@/stores/user';

export type CustomAccessRightsKey =
  | 'compliances'
  | `application:${string}`
  | 'policies'
  | 'actions'
  | 'actiondetails'
  | 'runactiondetails'
  | 'applications'
  | 'virtualmachines'
  | 'profiles'
  | 'blueprintbindings'
  | 'licenses';

const accessRightKey = (subject: CustomAccessRightsKey, verb: string) => `${verb}/${subject}`;

export const useCustomAccessRights = defineStore('customAccessRights', () => {
  const userStore = useUserStore();

  const customAccessRightsMap = ref<Map<string, boolean | undefined>>(new Map());

  const getCustomAccessRight = (verb: string, subject: CustomAccessRightsKey) =>
    customAccessRightsMap.value.get(accessRightKey(subject, verb)) ?? false;
  // We need this function to check if the access right has never been set, this is used in the policies view page to tell whether to show a no permissions message or no policies message
  const getUndefinableCustomAccessRight = (verb: string, subject: CustomAccessRightsKey) =>
    customAccessRightsMap.value.get(accessRightKey(subject, verb));
  const setCustomAccessRight = (
    verb: string,
    subject: CustomAccessRightsKey,
    isAllowed?: boolean,
  ) => customAccessRightsMap.value.set(accessRightKey(subject, verb), isAllowed);
  const isCustomAccessRightPending = (verb: string, subject: CustomAccessRightsKey) =>
    !customAccessRightsMap.value.has(accessRightKey(subject, verb));
  const resetCustomAccessRight = (verb: string, subject: CustomAccessRightsKey) =>
    customAccessRightsMap.value.delete(accessRightKey(subject, verb));

  const resetAll = () => (customAccessRightsMap.value = new Map());

  watch(
    () => userStore.authUser,
    (newUser) => {
      if (newUser === undefined) resetAll();
    },
  );

  return {
    getCustomAccessRight,
    setCustomAccessRight,
    isCustomAccessRightPending,
    resetAll,
    resetCustomAccessRight,
    getUndefinableCustomAccessRight,
  };
});
